import { AnimatedSprite, Container, IDestroyOptions, Point, Texture } from "pixi.js";
import { AnimationController } from "../AnimationController";
import { Event } from "event-system";
import { gsap } from "gsap";
import { SlowMo } from "gsap/EasePack";
import { CommonResourcesPackage } from "../../resources/CommonResourcesPackage";
import { CoinsTexturePack } from "./texturesPacks/CoinsTexturePack";

export class CoinsCollectAnimationView extends Container {
    private static readonly SPAWN_RADIUS: number = 50/*px*/;
    private static readonly MOVE_DURATION: number = 0.45/*s*/;
    private static readonly END_SCALE: number = 0.1/*unit*/;
    private static readonly SHOWING_DELAY: number = 0.03/*s*/;

    private readonly animationController: AnimationController;
    private readonly textures: Array<Texture>;
    private readonly itemsCount: number;
    private readonly collectPoint: Point;
    private coinCollectAnimations: Array<gsap.core.Timeline>;

    public isActive: boolean;

    public get coinsCollected(): Event {
        return this.animationController.animationDone;
    }

    public constructor(commonResources: CommonResourcesPackage, collectItemsCount: number, collectPoint: Point, perCoins: boolean = true) {
        super();
        this.animationController = new AnimationController();
        if (perCoins) {
            this.textures = new CoinsTexturePack(commonResources).rotationCoins;
        } else {
            this.textures = [commonResources.assets_common_ui_top_panel_token_icon_png];
        }
        this.itemsCount = collectItemsCount;
        this.collectPoint = collectPoint;
        this.coinCollectAnimations = [];
    }

    public async startAnimation(): Promise<void[]> {
        for (let i = 0; i < this.itemsCount; i++) {
            const coin = new AnimatedSprite(this.textures);
            coin.currentFrame = Math.floor(Math.random() * (coin.totalFrames - 1));
            const spawnPoint = this.getRandomSpawnPoint();
            coin.position.copyFrom(spawnPoint);
            coin.anchor.set(0.5, 0.5);
            coin.rotation = Math.random() * Math.PI * 2;
            coin.visible = false;
            this.addChild(coin);
            this.setupCollectAnimation(coin, i * CoinsCollectAnimationView.SHOWING_DELAY);
        }
        this.isActive = true;
        return await this.animationController.startAnimation();
    }

    public setAnimationProgress(progress: number): void {
        this.coinCollectAnimations?.forEach(timeLine => timeLine?.progress(progress));
    }

    private getRandomSpawnPoint(): Point {
        const theta = Math.random() * Math.PI * 2;
        return new Point(CoinsCollectAnimationView.SPAWN_RADIUS * Math.cos(theta) + 80, CoinsCollectAnimationView.SPAWN_RADIUS * Math.sin(theta) + 200);
    }

    private setupCollectAnimation(sprite: AnimatedSprite, showDelay: number): void {
        const startPosition = sprite.position.clone();
        const timeline = gsap.timeline();
        this.coinCollectAnimations.push(timeline);
        timeline.delay(showDelay);
        timeline.set(sprite, { visible: true });
        timeline.fromTo(sprite,
            {
                y: startPosition.y,
            },
            {
                y: this.collectPoint.y,
                duration: CoinsCollectAnimationView.MOVE_DURATION,
                ease: "none"
            });
        timeline.fromTo(sprite,
            {
                x: startPosition.x,
            },
            {
                x: this.collectPoint.x,
                duration: CoinsCollectAnimationView.MOVE_DURATION * 0.5,
                ease: "back.in(3)"
            }, "<");
        timeline.to(sprite,
            {
                x: this.collectPoint.x - 80,
                duration: CoinsCollectAnimationView.MOVE_DURATION * 0.5,
                ease: SlowMo.ease.config(0.05, 0.1, true)
            }, ">");
        const startScale = sprite.scale.clone();
        timeline.fromTo(sprite.scale,
            {
                x: startScale.x,
                y: startScale.y
            },
            {
                x: CoinsCollectAnimationView.END_SCALE,
                y: CoinsCollectAnimationView.END_SCALE,
                duration: CoinsCollectAnimationView.MOVE_DURATION * 0.25,
                ease: "power1.in"
            }, "<50%");
        const animation = timeline.then(() => {
            sprite.destroy();
            timeline?.kill();
        });
        this.animationController.addAnimation(animation);
    }

    override destroy(options?: boolean | IDestroyOptions): void {
        this.coinCollectAnimations.forEach(timeLine => timeLine?.kill());
        super.destroy(options);
    }
}
